import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
} from "@mui/material";
import globalServices from "../../services/globalServices";
import { showToast } from "../../components/common/toastNotification";
import PictureAsPdfIcon from "../../assets/images/PolicyPDF_Icon.svg";
import { useAppSelector } from "../../globalState/hooks";
import { LoginRootState } from "../../types/login";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomTooltip from "../../components/common/customTooltip";

interface TabItem {
  id: number;
  policyType: string;
  country: string;
}

interface DocumentItem {
  policyTypeId: number;
  documentName: string;
  docUrl: string;
}

interface DocumentCardProps {
  documentName: string;
  docUrl: string;
}

function DocumentCard({ documentName, docUrl }: DocumentCardProps) {
  return (
    <Card
      sx={{
        height: 250,
        borderRadius: 0,
        color: "#ffffff",
        backgroundColor: "#393939",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        cursor: "pointer",
        "&:hover": { boxShadow: 6 },
      }}
      onClick={() => window.open(docUrl, "_blank")}
    >
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#4F4F4F",
          m: 1,
        }}
      >
        <img
          src={PictureAsPdfIcon}
          alt="PDF Icon"
          style={{ width: 60, height: 60, objectFit: "contain" }}
        />
      </Box>
      <CardContent sx={{ padding: 1 }}>
        <CustomTooltip text={documentName} maxLength={20} fontSize="12px" />
      </CardContent>
    </Card>
  );
}

function Policies() {
  const [tabsData, setTabsData] = useState<TabItem[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(false);

  const userDetails = useAppSelector(
    (state: LoginRootState) => state.authReducer.loginDetials
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchPolicyData = useCallback(async () => {
    try {
      const userLocation = userDetails?.country;
      const res = await globalServices.getPolicyData(userLocation);
      if (res?.data?.success) {
        setTabsData(res.data.data);
      } else {
        showToast(res?.data?.message, "error");
      }
    } catch (error) {
      console.error("Error while fetching policy data:", error);
    }
  }, [userDetails?.country]);

  const fetchDocuments = useCallback(async () => {
    if (tabsData.length === 0) return;
    setLoading(true);
    try {
      const tabId = tabsData[selectedTab]?.id;
      const res = await globalServices.getPolicyDataById(tabId);
      if (res?.data?.success) {
        setDocuments(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, tabsData]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
    },
    []
  );

  useEffect(() => {
    fetchPolicyData();
  }, [fetchPolicyData]);


  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  if (userDetails?.country !== "India" && userDetails?.country !== "UK") {
    return (
      <Box sx={{ padding: 2, display: "flex", justifyContent: "center" }}>
        <Typography variant="h6" color="white">
          Please get in touch with your HRBP for Policy details.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      color="white"
      sx={{
        padding: 2,
        display: "flex",
        flexDirection: "column",
        height: { xs: "100%", sm: "calc(100vh - 64px)" },
      }}
    >
      <Typography className="f-16 f-bold" paddingBottom={2}>
        Policies & Procedures | {userDetails?.country}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexGrow: 1,
          flexWrap: "nowrap",
          minHeight: 0,
        }}
      >
        {/* Sidebar Tabs */}
        {isMobile ? (
          <Box sx={{ width: "100%", mb: 0 }}>
            <Accordion
              expanded={accordionExpanded}
              onChange={() => setAccordionExpanded(!accordionExpanded)}
              sx={{ backgroundColor: "#444", color: "#fff" }}
            >
              <AccordionSummary
                sx={{ backgroundColor: "#000000" }}
                expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
              >
                <Typography>
                  {tabsData[selectedTab]?.policyType || "Select Policy Type"}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {tabsData.map((item, index) => (
                  <MenuItem
                    key={item.id}
                    selected={selectedTab === index}
                    onClick={() => {
                      setSelectedTab(index);
                      setAccordionExpanded(false);
                    }}
                    sx={{
                      backgroundColor:
                        selectedTab === index
                          ? "#212121!important"
                          : "transparent",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#333",
                      },
                    }}
                  >
                    {item.policyType}
                  </MenuItem>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : (
          <Box
            sx={{
              width: 250,
              minWidth: 250,
              bgcolor: "#5D5D5D",
              borderRight: "1px solid #333",
              marginRight: 2,
              overflowY: "auto",
            }}
          >
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={selectedTab}
              onChange={handleChange}
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                ".MuiTab-root": {
                  alignItems: "flex-start",
                  color: "#ffffff",
                  textAlign: "left",
                  textTransform: "none",
                  fontSize: "14px",
                  "&.Mui-selected": {
                    color: "#ffffff",
                    backgroundColor: "#212121",
                    borderRight: "none",
                  },
                },
              }}
            >
              {tabsData.map((item) => (
                <Tab key={item.id} label={item.policyType} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Document Cards */}
        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: "#5D5D5D",
            padding: { xs: 0, sm: 2 },
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(6, 1fr)",
            },
            gap: 1,
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : (
            documents.map((doc, index) => (
              <DocumentCard
                key={index}
                documentName={doc.documentName}
                docUrl={doc.docUrl}
              />
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Policies;
